import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import * as React from 'react';

// ── Design tokens (shared by every landing section) ────────────────────────
export const TEAL = '#00bba7';
export const AMBER = '#ffae3b';
export const FONT_DISPLAY = '"Archivo Expanded", "Archivo", sans-serif';
export const FONT_BODY = '"Archivo", sans-serif';
export const FONT_MONO = '"Space Mono", monospace';

// Fonts + shared keyframes. Render once near the landing root (React 19 hoists
// <link>/<style> into <head>).
export function LandingFonts() {
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=Archivo+Expanded:wght@600;700;800&family=Space+Mono:wght@400;700&display=swap"
      />
      <style>{`
        @keyframes ds-rise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ds-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }
        @keyframes ds-flow { to { stroke-dashoffset: -56; } }
        .ds-rise { opacity: 0; animation: ds-rise 0.7s cubic-bezier(0.22,1,0.36,1) forwards; }
        .ds-flow { animation: ds-flow 1.1s linear infinite; }
      `}</style>
    </>
  );
}

// Subtle technical-grid background, masked to fade out. Drop into a section as
// an aria-hidden absolutely-positioned layer.
export const gridLayerSx = (theme) => ({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  backgroundImage:
    'linear-gradient(rgba(2,20,18,0.05) 1px, transparent 1px),' +
    'linear-gradient(90deg, rgba(2,20,18,0.05) 1px, transparent 1px)',
  backgroundSize: '54px 54px',
  maskImage: 'radial-gradient(circle at 50% 40%, black, transparent 78%)',
  WebkitMaskImage: 'radial-gradient(circle at 50% 40%, black, transparent 78%)',
  ...theme.applyStyles('dark', {
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),' +
      'linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
  }),
});

// Glass card surface, theme-aware. Spread into a card's sx.
export const glassCardSx = (theme) => ({
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'rgba(2,20,18,0.10)',
  backgroundColor: 'rgba(255,255,255,0.66)',
  backdropFilter: 'blur(12px)',
  boxShadow: '0 18px 44px rgba(2,20,18,0.10)',
  transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 26px 60px rgba(2,20,18,0.16)', borderColor: `${TEAL}55` },
  ...theme.applyStyles('dark', {
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(12,18,20,0.5)',
    boxShadow: '0 18px 44px rgba(0,0,0,0.4)',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 26px 60px rgba(0,0,0,0.55)', borderColor: `${TEAL}66` },
  }),
});

// Mono eyebrow tag with a blinking accent dot. Use above section titles.
// onDark: force the light-on-dark color regardless of the app color mode
// (for sections that are always dark, e.g. the Highlights band).
export function Eyebrow({ children, color = TEAL, sx, onDark = false }) {
  const litColor = color === TEAL ? '#7df0e2' : '#ffd79a';
  return (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderRadius: 999,
        border: '1px solid',
        borderColor: `${color}55`,
        backgroundColor: `${color}14`,
        fontFamily: FONT_MONO,
        fontSize: 11,
        letterSpacing: '0.14em',
        color: onDark ? litColor : '#0a3f39',
        ...(onDark ? {} : theme.applyStyles('dark', { color: litColor })),
        ...sx,
      })}
    >
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, animation: 'ds-blink 1.4s ease-in-out infinite' }} />
      {children}
    </Box>
  );
}

// Section heading block: eyebrow + Archivo Expanded uppercase title + subtitle.
export function SectionHeading({ overline, title, subtitle, align = 'left', accent = TEAL, maxWidth = 720, onDark = false }) {
  const centered = align === 'center';
  return (
    <Box sx={{ maxWidth, mx: centered ? 'auto' : 0, textAlign: { xs: 'left', md: centered ? 'center' : 'left' } }}>
      {overline && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'flex-start', md: centered ? 'center' : 'flex-start' } }}>
          <Eyebrow color={accent} onDark={onDark}>{overline}</Eyebrow>
        </Box>
      )}
      <Typography
        component="h2"
        sx={(theme) => ({
          fontFamily: FONT_DISPLAY,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '-0.01em',
          lineHeight: 1.05,
          fontSize: 'clamp(1.7rem, 2.7vw, 2.4rem)',
          color: onDark ? '#f2fbf9' : '#071513',
          ...(onDark ? {} : theme.applyStyles('dark', { color: '#f2fbf9' })),
        })}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={(theme) => ({
            mt: 2,
            fontFamily: FONT_BODY,
            fontSize: '1.05rem',
            lineHeight: 1.5,
            color: onDark ? '#9fc4bd' : '#33514c',
            ...(onDark ? {} : theme.applyStyles('dark', { color: '#9fc4bd' })),
          })}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// Scroll-triggered reveal. Wrap a block; it rises in when it enters the viewport.
export function Reveal({ children, delay = 0, sx }) {
  const ref = React.useRef(null);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(26px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
